import {announce} from '@github-ui/aria-live'
import type {
  RepoIssuesDashboardPinnedIssue,
  RepoIssuesDashboardPinnedIssues,
} from '@github-ui/dotcom-schema/types/issues/v1/repo_issues_dashboard'
import {
  prioritizePinnedIssuesMutation as prioritizePinnedIssuesQueryId,
  unpinIssueMutation as unpinIssueQueryId,
} from '@github-ui/server-persisted-queries'
import type {PrioritizePinnedIssuesMutationMutation} from '@github-ui/server-persisted-queries/types/prioritizePinnedIssuesMutation'
import type {UnpinIssueMutationMutation} from '@github-ui/server-persisted-queries/types/unpinIssueMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useRef, useState} from 'react'

const REORDER_ERROR = 'Could not reorder pinned issues. Please try again.'
const UNPIN_ERROR = 'Could not unpin issue. Please try again.'

type GraphQLResponse<TData> = {
  data?: TData | null
  errors?: ReadonlyArray<{message?: string}> | null
}

async function readGraphQLResponse<TData>(response: Response, errorMessage: string): Promise<TData> {
  if (!response.ok) throw new Error(`${errorMessage} (${response.status})`)
  const result = (await response.json()) as GraphQLResponse<TData>
  if (!result.data) throw new Error(result.errors?.[0]?.message ?? errorMessage)
  return result.data
}

async function reorderPinnedIssues(
  owner: string,
  repo: string,
  orderedIds: string[],
  issues: RepoIssuesDashboardPinnedIssue[],
): Promise<RepoIssuesDashboardPinnedIssue[]> {
  const pathname = `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/pinned_issues`
  const response = await reactFetchJSON(
    `${pathname}?${new URLSearchParams({queryId: prioritizePinnedIssuesQueryId})}`,
    {
      method: 'PUT',
      body: {issueIds: orderedIds},
    },
  )
  const data = await readGraphQLResponse<PrioritizePinnedIssuesMutationMutation>(response, REORDER_ERROR)
  const confirmedIds = data.prioritizePinnedIssues?.repository?.pinnedIssues?.nodes?.map(node => node?.issue.id)
  if (!confirmedIds || confirmedIds.some(id => !id) || confirmedIds.length !== orderedIds.length) {
    throw new Error(REORDER_ERROR)
  }

  const issuesById = new Map(issues.map(issue => [issue.id, issue]))
  const confirmedIssues = confirmedIds.flatMap(id => {
    const issue = id ? issuesById.get(id) : undefined
    return issue ? [issue] : []
  })
  if (confirmedIssues.length !== issues.length) throw new Error(REORDER_ERROR)
  return confirmedIssues
}

async function unpinIssue(
  owner: string,
  repo: string,
  issue: RepoIssuesDashboardPinnedIssue,
  remainingIssues: RepoIssuesDashboardPinnedIssue[],
): Promise<RepoIssuesDashboardPinnedIssue[]> {
  const pathname = `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issue.number}/pin`
  const response = await reactFetchJSON(`${pathname}?${new URLSearchParams({queryId: unpinIssueQueryId})}`, {
    method: 'DELETE',
  })
  const data = await readGraphQLResponse<UnpinIssueMutationMutation>(response, UNPIN_ERROR)
  if (data.unpinIssue?.issue?.id !== issue.id || data.unpinIssue.issue.isPinned !== false) {
    throw new Error(UNPIN_ERROR)
  }
  return remainingIssues
}

export type UsePinnedIssuesResult = {
  issues: RepoIssuesDashboardPinnedIssue[]
  viewerCanPinIssues: boolean
  reorder: (orderedIds: string[]) => void
  unpin: (id: string) => void
}

/**
 * Local pinned-strip state seeded from the layout payload. Reorder and unpin
 * apply optimistically and call the repository REST endpoints; on success the
 * server response reconciles the order, and on failure the previous order is
 * restored and the error is announced. The main issues list is never refetched.
 */
export function usePinnedIssues(
  owner: string,
  repo: string,
  source: RepoIssuesDashboardPinnedIssues | undefined,
): UsePinnedIssuesResult {
  const viewerCanPinIssues = source?.viewerCanPinIssues ?? false

  // Re-derive when the layout payload sends a new pinned message (reference
  // change); optimistic writes survive between those updates so the strip
  // reflects reorder/unpin immediately.
  const [issues, setIssues] = useState<RepoIssuesDashboardPinnedIssue[]>(() => source?.issues ?? [])
  const [prevSource, setPrevSource] = useState(source)
  if (source !== prevSource) {
    setPrevSource(source)
    setIssues(source?.issues ?? [])
  }

  // Only one mutation in flight at a time so responses can't apply out of order.
  const inFlightRef = useRef(false)

  const mutate = useCallback(
    async (
      previous: RepoIssuesDashboardPinnedIssue[],
      optimistic: RepoIssuesDashboardPinnedIssue[],
      request: () => Promise<RepoIssuesDashboardPinnedIssue[]>,
      errorMessage: string,
    ) => {
      if (inFlightRef.current) return
      inFlightRef.current = true
      setIssues(optimistic)
      try {
        setIssues(await request())
      } catch {
        setIssues(previous)
        announce(errorMessage)
      } finally {
        inFlightRef.current = false
      }
    },
    [],
  )

  const reorder = useCallback(
    (orderedIds: string[]) => {
      const byId = new Map(issues.map(issue => [issue.id, issue]))
      const optimistic = orderedIds.flatMap(id => {
        const issue = byId.get(id)
        return issue ? [issue] : []
      })
      void mutate(issues, optimistic, () => reorderPinnedIssues(owner, repo, orderedIds, issues), REORDER_ERROR)
    },
    [issues, mutate, owner, repo],
  )

  const unpin = useCallback(
    (id: string) => {
      const issue = issues.find(candidate => candidate.id === id)
      if (!issue) return
      const optimistic = issues.filter(candidate => candidate.id !== id)
      void mutate(issues, optimistic, () => unpinIssue(owner, repo, issue, optimistic), UNPIN_ERROR)
    },
    [issues, mutate, owner, repo],
  )

  return {issues, viewerCanPinIssues, reorder, unpin}
}
